import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Trophy, Home, RotateCcw, ArrowLeft, Volume2, VolumeX, CheckCircle, XCircle, User, Gift, Loader2 } from 'lucide-react';
import { useGame } from '../../context/GameContext';
import { useRewards } from '../../context/RewardsContext';
import { PointsButton } from '../../components/RewardsWidgets';
import { getSortingData } from '../../utils/mockData';
import soundEffects from '../../utils/soundEffects';
import CelebrationAnimation from '../../components/CelebrationAnimation';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PuzzleGame = () => {
  const navigate = useNavigate();
  const { selectedAge, selectedSubject, addScore, playerName } = useGame();
  const { awardCorrectAnswer, awardLessonComplete } = useRewards();
  const [sortingData, setSortingData] = useState([]);
  const [currentRound, setCurrentRound] = useState(0);
  const [items, setItems] = useState([]);
  const [category1Items, setCategory1Items] = useState([]);
  const [category2Items, setCategory2Items] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [correctSorts, setCorrectSorts] = useState(0);
  const [totalSorts, setTotalSorts] = useState(0);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);

  // Map age groups to API format
  const getAgeGroupForAPI = (age) => {
    const ageMap = {
      'preschool': 'preschool',
      '3-5': 'preschool',
      'elementary': 'elementary',
      '6-8': 'elementary',
      'advanced': 'advanced',
      '9-12': 'advanced'
    };
    return ageMap[age] || 'elementary';
  };

  // Get the best available voice
  const getBestVoice = useCallback(() => {
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) return null;
    
    const preferredVoices = [
      'Samantha', 'Google US English', 'Google UK English Female',
      'Microsoft Zira', 'Microsoft David', 'Karen', 'Moira', 'Fiona',
      'Victoria', 'Alex', 'Daniel', 'Tessa',
    ];
    
    for (const preferred of preferredVoices) {
      const voice = voices.find(v => v.name.includes(preferred));
      if (voice) return voice;
    }
    
    const englishVoice = voices.find(v => 
      v.lang.startsWith('en') && 
      (v.name.includes('Google') || v.name.includes('Microsoft') || v.name.includes('Natural') || !v.name.includes('eSpeak'))
    );
    if (englishVoice) return englishVoice;
    
    return voices.find(v => v.lang.startsWith('en')) || voices[0];
  }, []);

  // Load voices when available
  useEffect(() => {
    const loadVoices = () => {
      const voice = getBestVoice();
      if (voice) setSelectedVoice(voice);
    };
    
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [getBestVoice]);

  // Read aloud function with better voice
  const speakText = useCallback((text) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    window.speechSynthesis.speak(utterance);
  }, [selectedVoice]);

  // Load sorting content - try API first, fallback to mockData
  useEffect(() => {
    const loadSortingContent = async () => {
      setLoading(true);
      try {
        const ageGroup = getAgeGroupForAPI(selectedAge);
        const response = await axios.get(`${API}/content/sorting/${ageGroup}/${selectedSubject}`);
        
        if (response.data.categories && response.data.categories.length > 0) {
          // API data available - use it
          setSortingData(response.data.categories);
          console.log(`Loaded sorting data from API for ${ageGroup}/${selectedSubject}`);
        } else {
          // No API data - fallback to mockData
          console.log('No API data for sorting, falling back to mockData');
          const data = getSortingData(selectedAge, selectedSubject);
          setSortingData(data);
        }
      } catch (error) {
        console.log('API error for sorting, falling back to mockData:', error.message);
        const data = getSortingData(selectedAge, selectedSubject);
        setSortingData(data);
      }
      setLoading(false);
    };

    loadSortingContent();
  }, [selectedAge, selectedSubject]);

  useEffect(() => {
    if (sortingData.length > 0 && currentRound < sortingData.length) {
      initializeRound();
    }
  }, [sortingData, currentRound]);

  useEffect(() => {
    return () => window.speechSynthesis.cancel();
  }, []);

  const initializeRound = () => {
    const round = sortingData[currentRound];
    if (!round) return;
    // Shuffle items
    const shuffled = [...round.items].sort(() => Math.random() - 0.5);
    setItems(shuffled);
    setCategory1Items([]);
    setCategory2Items([]);
    setSelectedItem(null);
    setFeedback(null);
    setIsComplete(false);
  };

  // Check if all items are sorted
  useEffect(() => {
    if (sortingData.length > 0 && items.length === 0 && (category1Items.length > 0 || category2Items.length > 0)) {
      // Check if sorting is correct
      const round = sortingData[currentRound];
      const cat1Correct = category1Items.every(item => item.category === round.category1.name);
      const cat2Correct = category2Items.every(item => item.category === round.category2.name);
      
      if (cat1Correct && cat2Correct) {
        setIsComplete(true);
        const points = 50;
        setScore(prev => prev + points);
        addScore(points);
        speakText('Amazing! You sorted everything correctly!');
      }
    }
  }, [items, category1Items, category2Items]);

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setFeedback(null);
  };

  const handleCategoryClick = (categoryNum) => {
    if (!selectedItem) return;

    const round = sortingData[currentRound];
    const targetCategory = categoryNum === 1 ? round.category1.name : round.category2.name;
    const isCorrect = selectedItem.category === targetCategory;

    setTotalSorts(prev => prev + 1);

    if (isCorrect) {
      setCorrectSorts(prev => prev + 1);
      // Remove from items and add to category
      setItems(prev => prev.filter(i => i.id !== selectedItem.id));
      if (categoryNum === 1) {
        setCategory1Items(prev => [...prev, selectedItem]);
      } else {
        setCategory2Items(prev => [...prev, selectedItem]);
      }
      setFeedback({ type: 'success', message: 'Correct! 🎉' });
      setScore(prev => prev + 10);
      addScore(10);
      // Award +2 points for correct sort
      awardCorrectAnswer();
      // Play correct sound
      if (soundEnabled) soundEffects.playCorrect();
    } else {
      setFeedback({ type: 'error', message: `Oops! Try again! 🤔` });
      // Play incorrect sound
      if (soundEnabled) soundEffects.playIncorrect();
    }
    setSelectedItem(null);
  };

  const nextRound = () => {
    // Award +10 points for completing the round
    awardLessonComplete('sorting');
    if (soundEnabled) soundEffects.playCelebration();
    if (currentRound + 1 < sortingData.length) {
      setCurrentRound(currentRound + 1);
    } else {
      // Show celebration animation
      setShowCelebration(true);
    }
  };

  const handleCelebrationComplete = () => {
    setShowCelebration(false);
    navigate('/result', {
      state: {
        score,
        totalQuestions: sortingData.length,
        correctAnswers: sortingData.length,
        gameType: 'puzzle'
      }
    });
  };

  const resetRound = () => {
    initializeRound();
  };

  const handleBack = () => {
    if (selectedAge === 'preschool') {
      navigate('/preschool-topics');
    } else {
      navigate('/subject-selection');
    }
  };

  if (loading || sortingData.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Card className="p-8 bg-white/95 backdrop-blur-sm shadow-2xl text-center max-w-md" style={{ borderRadius: '28px' }}>
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-purple-600 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-800">Loading Sorting Game...</h2>
        </Card>
      </div>
    );
  }

  const currentData = sortingData[currentRound];

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      {/* Celebration Animation */}
      <CelebrationAnimation 
        show={showCelebration} 
        onComplete={handleCelebrationComplete}
        message="Sorting Star!"
        duration={3500}
      />

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <Button
            onClick={handleBack}
            variant="ghost"
            className="text-lg font-semibold hover:scale-110 transition-transform bg-white/50 hover:bg-white/70"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2 md:gap-4">
            <PointsButton />
            <Button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-full hover:scale-105 transition-transform shadow-lg"
            >
              <User className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => navigate('/rewards')}
              className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-full hover:scale-105 transition-transform shadow-lg"
            >
              <Gift className="w-4 h-4" />
            </Button>
            <Card className="px-4 py-2 bg-white/95" style={{ borderRadius: '12px' }}>
              <Trophy className="w-5 h-5 inline mr-2 text-yellow-600" />
              <span className="font-bold text-lg">{score}</span>
            </Card>
          </div>
        </div>

        <Card className="p-6 md:p-8 bg-white/95 backdrop-blur-sm shadow-2xl" style={{ borderRadius: '28px' }}>
          {/* Title */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-3 mb-2">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
                🗂️ Sorting Game - Round {currentRound + 1} of {sortingData.length}
              </h2>
              <button
                onClick={() => speakText(`Sort the items! Put each item in the correct category. ${currentData.category1.name} or ${currentData.category2.name}. Click an item, then click where it belongs!`)}
                className={`p-2 rounded-full transition-all duration-300 hover:scale-110 ${
                  isSpeaking 
                    ? 'bg-purple-500 text-white animate-pulse' 
                    : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                }`}
                title="Read instructions aloud"
              >
                <Volume2 className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xl text-gray-600 font-medium">
              {currentData.instruction}
            </p>
            <p className="text-sm text-gray-500 mt-1">Click an item, then click the category it belongs to!</p>
          </div>

          {/* Feedback */}
          {feedback && (
            <div className={`text-center mb-4 p-3 rounded-xl animate-bounce ${
              feedback.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              <span className="text-xl font-bold">{feedback.message}</span>
            </div>
          )}

          {/* Items to Sort */}
          {!isComplete && items.length > 0 && (
            <div className="mb-6">
              <p className="text-center text-gray-600 font-semibold mb-3">Items to Sort:</p>
              <div className="flex flex-wrap justify-center gap-3">
                {items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    className={`
                      px-4 py-3 rounded-xl text-lg font-bold transition-all duration-200
                      ${selectedItem?.id === item.id 
                        ? 'bg-yellow-400 text-gray-800 scale-110 ring-4 ring-yellow-300' 
                        : 'bg-gradient-to-br from-blue-400 to-purple-500 text-white hover:scale-105'
                      }
                    `}
                  >
                    <span className="text-2xl mr-2">{item.emoji}</span>
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Category 1 */}
            <div
              onClick={() => handleCategoryClick(1)}
              className={`
                p-6 rounded-2xl border-4 border-dashed transition-all duration-200 cursor-pointer min-h-[200px]
                ${selectedItem ? 'hover:border-solid hover:bg-green-50 border-green-400' : 'border-gray-300'}
                ${!selectedItem && 'cursor-default'}
              `}
              style={{ backgroundColor: currentData.category1.color + '20' }}
            >
              <div className="text-center mb-4">
                <span className="text-4xl">{currentData.category1.emoji}</span>
                <h3 className="text-2xl font-bold mt-2" style={{ color: currentData.category1.color }}>
                  {currentData.category1.name}
                </h3>
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {category1Items.map((item) => (
                  <span
                    key={item.id}
                    className="px-3 py-2 bg-white rounded-lg shadow text-lg font-semibold flex items-center gap-2"
                  >
                    <span className="text-xl">{item.emoji}</span>
                    {item.name}
                  </span>
                ))}
              </div>
              {category1Items.length === 0 && (
                <p className="text-center text-gray-400 mt-4">Drop items here!</p>
              )}
            </div>

            {/* Category 2 */}
            <div
              onClick={() => handleCategoryClick(2)}
              className={`
                p-6 rounded-2xl border-4 border-dashed transition-all duration-200 cursor-pointer min-h-[200px]
                ${selectedItem ? 'hover:border-solid hover:bg-orange-50 border-orange-400' : 'border-gray-300'}
                ${!selectedItem && 'cursor-default'}
              `}
              style={{ backgroundColor: currentData.category2.color + '20' }}
            >
              <div className="text-center mb-4">
                <span className="text-4xl">{currentData.category2.emoji}</span>
                <h3 className="text-2xl font-bold mt-2" style={{ color: currentData.category2.color }}>
                  {currentData.category2.name}
                </h3>
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {category2Items.map((item) => (
                  <span
                    key={item.id}
                    className="px-3 py-2 bg-white rounded-lg shadow text-lg font-semibold flex items-center gap-2"
                  >
                    <span className="text-xl">{item.emoji}</span>
                    {item.name}
                  </span>
                ))}
              </div>
              {category2Items.length === 0 && (
                <p className="text-center text-gray-400 mt-4">Drop items here!</p>
              )}
            </div>
          </div>

          {/* Completion Message */}
          {isComplete && (
            <div className="mt-6 text-center animate-bounce">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-6 py-3 rounded-full text-xl font-bold">
                <CheckCircle className="w-6 h-6" />
                🎉 Great job, {playerName}! All sorted correctly!
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <Button
              onClick={resetRound}
              variant="outline"
              className="px-6 py-3 text-lg font-bold border-2 hover:scale-105 transition-all"
              style={{ borderRadius: '12px' }}
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              Start Over
            </Button>

            {isComplete && (
              <Button
                onClick={nextRound}
                className="px-8 py-3 text-lg font-bold hover:scale-105 transition-all"
                style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '12px'
                }}
              >
                {currentRound + 1 < sortingData.length ? 'Next Round ➡️' : 'Finish! 🏆'}
              </Button>
            )}
          </div>
        </Card>

      </div>
    </div>
  );
};

export default PuzzleGame;
